import { useEffect, useRef, useState } from 'react';
import type { Article } from '../type/Article';
import type { TraceabilityApiClient } from '../api/TraceabilityApiClient';
import { useTranslation } from '@/hooks/useTranslation';

const DEFAULT_ARTICLE = (companyName: string): Article => ({
  name: '',
  companyName,
  archived: false,
});

export function useArticleManager(
  companyName: string,
  traceabilityApiClient: TraceabilityApiClient | null
) {
  const { t } = useTranslation();

  const [articles, setArticles] = useState<Article[]>([]);
  const [formArticle, setFormArticle] = useState<Article>(() => DEFAULT_ARTICLE(companyName));
  const [selectedId, setSelectedId] = useState<number | undefined>(undefined);
  const [formError, setFormError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [archiveConfirm, setArchiveConfirm] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const writeControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      writeControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    setArchiveConfirm(false);
  }, [selectedId]);

  useEffect(() => {
    if (!traceabilityApiClient) return;
    const ac = new AbortController();
    setIsLoading(true);
    setFormError(null);

    traceabilityApiClient
      .articleReadAll({}, { signal: ac.signal })
      .then((response) => {
        if (ac.signal.aborted) return;
        if (response.ok && response.data?.articles) {
          setArticles(response.data.articles.map((item: { article: Article }) => item.article));
        } else {
          if (!response.ok && response.code === 401) return;
          setFormError(
            response.ok
              ? t('traceability.articleManager.errors.noData')
              : response.message || t('traceability.articleManager.errors.fetchFailed')
          );
        }
      })
      .catch((err: unknown) => {
        if (ac.signal.aborted) return;
        console.error('Failed to fetch articles:', err);
        setFormError(t('traceability.articleManager.errors.fetchFailed'));
      })
      .finally(() => {
        if (!ac.signal.aborted) setIsLoading(false);
      });

    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [traceabilityApiClient]);

  const refreshArticles = async (signal: AbortSignal) => {
    if (!traceabilityApiClient) return;
    setIsLoading(true);
    try {
      const response = await traceabilityApiClient.articleReadAll({});
      if (signal.aborted) return;
      if (response.ok && response.data?.articles) {
        setArticles(response.data.articles.map((item: { article: Article }) => item.article));
      }
    } catch (err: unknown) {
      console.error('Failed to refresh articles:', err);
    } finally {
      if (!signal.aborted) setIsLoading(false);
    }
  };

  const handleAddOrUpdate = async () => {
    if (!traceabilityApiClient) {
      setFormError(t('traceability.articleManager.errors.apiNotReady'));
      return;
    }
    const trimmedName = formArticle.name.trim();
    if (!trimmedName) {
      setFormError(t('traceability.articleManager.errors.nameRequired'));
      return;
    }

    writeControllerRef.current?.abort();
    const ac = new AbortController();
    writeControllerRef.current = ac;
    setIsSaving(true);
    setFormError(null);

    try {
      const payload: Article = {
        ...(formArticle.id !== undefined && { id: formArticle.id }),
        name: trimmedName,
        companyName,
        archived: formArticle.id !== undefined ? formArticle.archived : false,
      };

      const response = await traceabilityApiClient.articleWrite(
        { article: payload },
        { signal: ac.signal }
      );
      if (ac.signal.aborted) return;

      if (response.ok) {
        const savedArticle = { ...payload, id: response.data.id };
        await refreshArticles(ac.signal);
        if (ac.signal.aborted) return;
        setFormArticle(savedArticle);
        setSelectedId(response.data.id);
      } else {
        if (response.code === 401) return;
        setFormError(response.message || t('traceability.articleManager.errors.saveFailed'));
      }
    } catch (error) {
      if (ac.signal.aborted) return;
      console.error('Failed to save article:', error);
      setFormError(t('traceability.articleManager.errors.saveFailed'));
    } finally {
      if (!ac.signal.aborted) setIsSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!archiveConfirm || !traceabilityApiClient || selectedId === undefined) return;
    const selectedArticle = articles.find((a) => a.id === selectedId);
    if (!selectedArticle) return;

    writeControllerRef.current?.abort();
    const ac = new AbortController();
    writeControllerRef.current = ac;
    setIsArchiving(true);
    setFormError(null);

    try {
      const response = await traceabilityApiClient.articleWrite(
        { article: { ...selectedArticle, archived: true } },
        { signal: ac.signal }
      );
      if (ac.signal.aborted) return;

      if (response.ok) {
        setArchiveConfirm(false);
        await refreshArticles(ac.signal);
        if (ac.signal.aborted) return;
        setSelectedId(undefined);
        setFormArticle(DEFAULT_ARTICLE(companyName));
      } else {
        if (response.code === 401) return;
        setFormError(response.message || t('traceability.articleManager.errors.saveFailed'));
      }
    } catch (error) {
      if (ac.signal.aborted) return;
      console.error('Failed to archive article:', error);
      setFormError(t('traceability.articleManager.errors.saveFailed'));
    } finally {
      if (!ac.signal.aborted) setIsArchiving(false);
    }
  };

  const handleRestore = async () => {
    if (!traceabilityApiClient || selectedId === undefined) return;
    const selectedArticle = articles.find((a) => a.id === selectedId);
    if (!selectedArticle) return;

    writeControllerRef.current?.abort();
    const ac = new AbortController();
    writeControllerRef.current = ac;
    setIsArchiving(true);
    setFormError(null);

    try {
      const response = await traceabilityApiClient.articleWrite(
        { article: { ...selectedArticle, archived: false } },
        { signal: ac.signal }
      );
      if (ac.signal.aborted) return;

      if (response.ok) {
        await refreshArticles(ac.signal);
        if (ac.signal.aborted) return;
        setSelectedId(undefined);
        setFormArticle(DEFAULT_ARTICLE(companyName));
      } else {
        if (response.code === 401) return;
        setFormError(response.message || t('traceability.articleManager.errors.saveFailed'));
      }
    } catch (error) {
      if (ac.signal.aborted) return;
      console.error('Failed to restore article:', error);
      setFormError(t('traceability.articleManager.errors.saveFailed'));
    } finally {
      if (!ac.signal.aborted) setIsArchiving(false);
    }
  };

  const handleSelectArticle = (article: Article) => {
    setFormError(null);
    setFormArticle(article);
    setSelectedId(article.id);
  };

  const handleNewArticle = () => {
    setFormError(null);
    setFormArticle(DEFAULT_ARTICLE(companyName));
    setSelectedId(undefined);
  };

  const selectedArticle = articles.find((a) => a.id === selectedId);
  const isEditing = formArticle.id !== undefined;
  const visibleArticles = showArchived ? articles : articles.filter((a) => !a.archived);
  const activeArticles = articles.filter((a) => !a.archived);

  return {
    articles,
    formArticle,
    setFormArticle,
    selectedId,
    formError,
    isLoading,
    isSaving,
    isArchiving,
    archiveConfirm,
    setArchiveConfirm,
    showArchived,
    setShowArchived,
    selectedArticle,
    isEditing,
    visibleArticles,
    activeArticles,
    handleAddOrUpdate,
    handleArchive,
    handleRestore,
    handleSelectArticle,
    handleNewArticle,
  };
}
